import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FaTimes } from 'react-icons/fa';

// Phase 3: rewritten from inline style objects to Tailwind classes (same API).
// Focus state aligned with the house input style (primary border + ring)
// instead of the old slate-900 border. New code should prefer common/Input
// (+ FormField) — TwInput stays for its clear-button / left-icon extras.
const SIZES = {
  sm: { box: 'tw-h-8 tw-text-xs', padX: 'tw-px-3', padLeft: 'tw-pl-8', padRight: 'tw-pr-8', icon: 11, btn: 'tw-w-[22px] tw-h-[22px]' },
  md: { box: 'tw-h-[38px] tw-text-[13px]', padX: 'tw-px-3.5', padLeft: 'tw-pl-9', padRight: 'tw-pr-9', icon: 12, btn: 'tw-w-6 tw-h-6' },
  lg: { box: 'tw-h-11 tw-text-sm', padX: 'tw-px-4', padLeft: 'tw-pl-10', padRight: 'tw-pr-10', icon: 14, btn: 'tw-w-7 tw-h-7' },
};

const TwInput = forwardRef(({
  value, onChange, onClear, type = 'text', size = 'sm',
  leftIcon: LeftIcon, placeholder = '', disabled = false,
  error = '', label = '', required = false,
  className = '', inputClassName = '', ...rest
}, ref) => {
  const sz = SIZES[size];
  const showClear = typeof onClear === 'function' && value;

  return (
    <div className={`tw-flex tw-flex-col tw-gap-1 ${className}`}>
      {label && (
        <label className="tw-text-xs tw-font-bold tw-text-slate-700">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </label>
      )}

      <div className="tw-relative tw-w-full">
        {LeftIcon && (
          <div className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none tw-flex tw-items-center">
            <LeftIcon size={sz.icon} />
          </div>
        )}

        <input
          ref={ref}
          type={type}
          value={value ?? ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={classNames(
            'tw-w-full tw-py-0 tw-m-0 tw-font-medium tw-leading-none tw-bg-white tw-text-slate-900',
            'tw-border tw-border-solid tw-rounded-lg tw-outline-none tw-transition-colors',
            'placeholder:tw-text-slate-400',
            'disabled:tw-bg-slate-50 disabled:tw-text-slate-400 disabled:tw-cursor-not-allowed',
            error
              ? 'tw-border-red-300 focus:tw-border-red-500 focus:tw-ring-1 focus:tw-ring-red-200'
              : 'tw-border-slate-200 focus:tw-border-primary-500 focus:tw-ring-1 focus:tw-ring-primary-200',
            sz.box,
            LeftIcon ? sz.padLeft : sz.padX,
            showClear ? sz.padRight : sz.padX,
            inputClassName
          )}
          {...rest}
        />

        {showClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear"
            className={classNames(
              'tw-absolute tw-right-1.5 tw-top-1/2 -tw-translate-y-1/2 tw-p-0 tw-m-0 tw-border-0',
              'tw-bg-transparent tw-text-slate-400 hover:tw-text-slate-600 tw-rounded-md',
              'tw-flex tw-items-center tw-justify-center',
              sz.btn
            )}
          >
            <FaTimes size={sz.icon - 2} />
          </button>
        )}
      </div>

      {error && <p className="tw-text-xs tw-text-red-600 tw-m-0">{error}</p>}
    </div>
  );
});

TwInput.displayName = 'TwInput';

TwInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  type: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  leftIcon: PropTypes.elementType,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default TwInput;
